/* eslint-disable @typescript-eslint/no-explicit-any */

import { shouldPersistClientAuthInLocalStorage } from "@/app/lib/auth/client-session-policy";
import { readJwtPayloadLoose } from "@/app/lib/auth/read-jwt-payload-client";

const TOKEN_KEYS = ["token", "auth_token"] as const;
const USER_KEYS = ["user", "current_user"] as const;

function clearKeysFromStorage(storage: Storage, keys: readonly string[]) {
  for (const k of keys) {
    storage.removeItem(k);
  }
}

/** Remove token keys from both storages so a new login policy does not leave stale credentials. */
function clearTokensFromBothStorages() {
  if (typeof window === "undefined") return;
  clearKeysFromStorage(sessionStorage, TOKEN_KEYS);
  clearKeysFromStorage(localStorage, TOKEN_KEYS);
}

function clearUsersFromBothStorages() {
  if (typeof window === "undefined") return;
  clearKeysFromStorage(sessionStorage, USER_KEYS);
  clearKeysFromStorage(localStorage, USER_KEYS);
}

function pickAuthStorage(rememberMe: boolean, roleId?: number | null): Storage {
  if (typeof window === "undefined") {
    throw new Error("pickAuthStorage called without window");
  }
  if (shouldPersistClientAuthInLocalStorage(roleId)) {
    return localStorage;
  }
  return rememberMe ? localStorage : sessionStorage;
}

export function setToken(
  token: string,
  rememberMe = false,
  roleId?: number | null
): void {
  console.log("[setToken] Storing token...", { rememberMe, roleId });
  if (typeof window !== "undefined") {
    clearTokensFromBothStorages();
    const storage = pickAuthStorage(rememberMe, roleId);
    storage.setItem("token", token);
    storage.setItem("auth_token", token);
  }
}

export function getToken(): string | null {
  console.log("[getToken] Retrieving token...");
  if (typeof window === "undefined") {
    console.log("[getToken] Server-side, returning null");
    return null;
  }

  for (const k of TOKEN_KEYS) {
    const t = sessionStorage.getItem(k);
    if (t) return t;
  }
  for (const k of TOKEN_KEYS) {
    const t = localStorage.getItem(k);
    if (t) return t;
  }

  console.log("[getToken] Retrieved token:", "Missing");
  return null;
}

/**
 * Authorization headers for API calls. Omits Bearer when sessionStorage/localStorage
 * has no token so same-origin requests can authenticate via the HttpOnly mtp_session cookie.
 */
export function authorizationHeaders(): Record<string, string> {
  const token = getToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/** True when the session token is stored in localStorage (remember-me login). */
export function getAuthRememberMe(): boolean {
  if (typeof window === "undefined") return false;
  for (const k of TOKEN_KEYS) {
    if (localStorage.getItem(k)) return true;
  }
  return false;
}

export function removeToken(): void {
  console.log("[removeToken] Removing token...");
  if (typeof window !== "undefined") {
    clearTokensFromBothStorages();
  }
}

export function setUser(
  user: any,
  rememberMe = false,
  roleId?: number | null
): void {
  const resolvedRoleId = roleId ?? user?.role?.id ?? user?.role_id ?? null;
  console.log("[setUser] Storing user data...", {
    rememberMe,
    roleId: resolvedRoleId,
  });
  if (typeof window !== "undefined") {
    clearUsersFromBothStorages();
    const storage = pickAuthStorage(rememberMe, resolvedRoleId);
    const serialized = JSON.stringify(user);
    storage.setItem("user", serialized);
    storage.setItem("current_user", serialized);
    cachedUser = user;
    cacheTimestamp = Date.now();
  }
}

/**
 * One-time migration: copy client credentials from sessionStorage to localStorage
 * when JWT is still valid (pre-48h-policy sessions).
 */
export function migrateClientSessionToLocalStorageIfNeeded(): void {
  if (typeof window === "undefined") return;

  let token: string | null = null;
  for (const k of TOKEN_KEYS) {
    token = sessionStorage.getItem(k);
    if (token) break;
  }
  if (!token) return;

  const payload = readJwtPayloadLoose(token);
  const exp = payload?.exp;
  if (exp != null && typeof exp === "number" && exp * 1000 <= Date.now()) {
    return;
  }

  let userStr: string | null = null;
  for (const k of USER_KEYS) {
    userStr = sessionStorage.getItem(k);
    if (userStr) break;
  }

  let roleId: number | null = null;
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      roleId = user?.role?.id ?? user?.role_id ?? null;
    } catch {
      return;
    }
  }

  if (!shouldPersistClientAuthInLocalStorage(roleId)) return;

  for (const k of TOKEN_KEYS) {
    const t = sessionStorage.getItem(k);
    if (t) localStorage.setItem(k, t);
  }
  for (const k of USER_KEYS) {
    const u = sessionStorage.getItem(k);
    if (u) localStorage.setItem(k, u);
  }
  clearKeysFromStorage(sessionStorage, TOKEN_KEYS);
  clearKeysFromStorage(sessionStorage, USER_KEYS);
}

// Cache user to avoid repeated localStorage reads
let cachedUser: any | null = null;
let cacheTimestamp: number = 0;
let lastLogTime: number = 0;
const CACHE_DURATION = 1000; // 1 second cache
const LOG_THROTTLE = 5000; // Only log every 5 seconds max

export function getUser(): any | null {
  // Only log in development and throttle logging
  const shouldLog = process.env.NODE_ENV === "development";
  const now = Date.now();

  // Use cache if available and fresh
  if (cachedUser !== null && now - cacheTimestamp < CACHE_DURATION) {
    return cachedUser;
  }

  if (shouldLog && now - lastLogTime > LOG_THROTTLE) {
    console.log("[getUser] Retrieving user data...");
    lastLogTime = now;
  }

  if (typeof window === "undefined") {
    return null;
  }

  let userStr: string | null = null;
  for (const k of USER_KEYS) {
    userStr = sessionStorage.getItem(k);
    if (userStr) break;
  }
  if (!userStr) {
    for (const k of USER_KEYS) {
      userStr = localStorage.getItem(k);
      if (userStr) break;
    }
  }

  if (!userStr) {
    cachedUser = null;
    cacheTimestamp = now;
    return null;
  }

  try {
    const user = JSON.parse(userStr);
    cachedUser = user;
    cacheTimestamp = now;

    if (shouldLog && now - lastLogTime > LOG_THROTTLE) {
      console.log("[getUser] Retrieved user:", {
        id: user.id,
        email: user.email,
      });
      lastLogTime = now;
    }
    return user;
  } catch (error) {
    console.error("[getUser] Error parsing user data:", error);
    cachedUser = null;
    cacheTimestamp = now;
    return null;
  }
}

export function removeUser(): void {
  console.log("[removeUser] Removing user data...");
  if (typeof window !== "undefined") {
    clearUsersFromBothStorages();
  }
}

export function setOriginalAdmin(admin: any): void {
  console.log("[setOriginalAdmin] Storing original admin data...");
  if (typeof window !== "undefined") {
    localStorage.setItem("original_admin", JSON.stringify(admin));
  }
}

export function getOriginalAdmin(): any | null {
  console.log("[getOriginalAdmin] Retrieving original admin data...");
  if (typeof window === "undefined") {
    console.log("[getOriginalAdmin] Server-side, returning null");
    return null;
  }

  const adminStr = localStorage.getItem("original_admin");

  if (!adminStr) {
    console.log("[getOriginalAdmin] No original admin data found");
    return null;
  }

  try {
    const admin = JSON.parse(adminStr);
    console.log("[getOriginalAdmin] Retrieved original admin:", {
      id: admin.id,
      email: admin.email,
    });
    return admin;
  } catch (error) {
    console.error("[getOriginalAdmin] Error parsing original admin data:", error);
    return null;
  }
}

export function clearOriginalAdmin(): void {
  console.log("[clearOriginalAdmin] Removing original admin data...");
  if (typeof window !== "undefined") {
    localStorage.removeItem("original_admin");
  }
}
